/**
 * Built-in monorepo starter for Zolaid Computer → Code view.
 * Users refine with AI instead of bootstrapping from zero.
 * Live Preview bundles frontend/* in the same order as the website generator.
 */

const FRONTEND_HTML = "frontend/index.html";

/** Sections with one `frontend/<id>.css` + `frontend/<id>.js` each (scroll + data attribute). */
const STARTER_SECTION_IDS = [
  "product",
  "overview",
  "features",
  "apps",
  "download",
  "teams",
  "enterprise",
  "education",
  "integrations",
  "resources",
  "docs",
  "tech-articles",
  "events",
  "blog",
  "support",
  "company",
  "careers",
  "delete-my-data",
  "terms",
  "privacy",
];

function starterSectionCss(id) {
  return `/* frontend/${id}.css — #${id} */
#${id}.section {
  scroll-margin-top: 5rem;
}
`;
}

function starterSectionJs(id) {
  return `/* frontend/${id}.js */
(function () {
  var el = document.getElementById("${id}");
  if (el) el.setAttribute("data-section-script", "${id}.js");
})();
`;
}

function buildStarterSectionFiles() {
  /** @type {Record<string, string>} */
  const out = {};
  for (const id of STARTER_SECTION_IDS) {
    out[`frontend/${id}.css`] = starterSectionCss(id);
    out[`frontend/${id}.js`] = starterSectionJs(id);
  }
  return out;
}

const FRONTEND_CSS_ORDER = [
  "frontend/styles.css",
  "frontend/product.css",
  "frontend/overview.css",
  "frontend/features.css",
  "frontend/apps.css",
  "frontend/pricing.css",
  "frontend/stripe-integration.css",
  "frontend/download.css",
  "frontend/teams.css",
  "frontend/enterprise.css",
  "frontend/education.css",
  "frontend/integrations.css",
  "frontend/resources.css",
  "frontend/docs.css",
  "frontend/tech-articles.css",
  "frontend/events.css",
  "frontend/blog.css",
  "frontend/support.css",
  "frontend/company.css",
  "frontend/about.css",
  "frontend/careers.css",
  "frontend/auth-integration.css",
  "frontend/contact.css",
  "frontend/delete-my-data.css",
  "frontend/terms.css",
  "frontend/privacy.css",
];

const FRONTEND_JS_ORDER = [
  "frontend/js/config.js",
  "frontend/js/api.js",
  "frontend/product.js",
  "frontend/overview.js",
  "frontend/features.js",
  "frontend/apps.js",
  "frontend/pricing.js",
  "frontend/stripe-integration.js",
  "frontend/download.js",
  "frontend/teams.js",
  "frontend/enterprise.js",
  "frontend/education.js",
  "frontend/integrations.js",
  "frontend/resources.js",
  "frontend/docs.js",
  "frontend/tech-articles.js",
  "frontend/events.js",
  "frontend/blog.js",
  "frontend/support.js",
  "frontend/company.js",
  "frontend/about.js",
  "frontend/careers.js",
  "frontend/auth-integration.js",
  "frontend/contact.js",
  "frontend/delete-my-data.js",
  "frontend/terms.js",
  "frontend/privacy.js",
  "frontend/app.js",
];

const CONFIG_JS = `/* frontend/js/config.js — public values only; never put secret keys here */
(function () {
  var w = typeof window !== "undefined" ? window : null;
  if (!w) return;
  var base = String(w.__API_BASE__ || "").trim().replace(/\\/$/, "");
  w.__STARTER_PUBLIC__ = Object.freeze({
    apiBase: base || "http://localhost:3001",
  });
})();
`;

const API_CLIENT_JS = `/* frontend/js/api.js — call your API; secrets live in backend .env only */
(function () {
  function apiRoot() {
    var p = typeof window !== "undefined" && window.__STARTER_PUBLIC__;
    var b = p && p.apiBase ? String(p.apiBase) : "http://localhost:3001";
    return b.replace(/\\/$/, "");
  }
  window.__starterApi = {
    json: function (path, init) {
      var p = path.charAt(0) === "/" ? path : "/" + path;
      var opt = init || {};
      var headers = Object.assign({ Accept: "application/json" }, opt.headers || {});
      if (opt.body && typeof opt.body === "string" && !headers["Content-Type"]) {
        headers["Content-Type"] = "application/json";
      }
      return fetch(apiRoot() + p, Object.assign({ credentials: "omit", mode: "cors" }, opt, { headers: headers })).then(
        function (res) {
          return res.text().then(function (text) {
            var data = {};
            try {
              data = text ? JSON.parse(text) : {};
            } catch (e) {
              data = { error: text || "Invalid JSON" };
            }
            if (!res.ok) throw new Error(data.error || res.statusText || "Request failed");
            return data;
          });
        }
      );
    },
  };
})();
`;

const ROOT_GITIGNORE = `# Dependencies & local secrets
node_modules/
.env
.env.local
.env.*.local
*.pem
.DS_Store
Thumbs.db

# Builds
dist/
build/
.cache/

# Logs
*.log
npm-debug.log*
`;

const FRONTEND_GITIGNORE = `node_modules/
.env
.env.local
*.log
`;

const FRONTEND_PUBLIC_ROBOTS = `User-agent: *
Allow: /

# Sitemap: set your real origin after deploy
# Sitemap: https://your-domain.example/sitemap.xml
`;

const FRONTEND_MANIFEST = `{
  "name": "Your product",
  "short_name": "Product",
  "start_url": "/",
  "display": "standalone",
  "background_color": "#0f1419",
  "theme_color": "#6366f1",
  "description": "Replace with your marketing copy.",
  "icons": []
}
`;

const FRONTEND_ASSETS_README = `Add real favicon.ico, favicon.svg, og-image.png here after branding.
Do not commit secrets; reference static assets only.
`;

const VERCEL_JSON = `{
  "rewrites": [{ "source": "/(.*)", "destination": "/index.html" }]
}
`;

const NETLIFY_TOML = `[build]
  publish = "frontend"
  command = "echo 'No build step — static HTML'"

[[redirects]]
  from = "/*"
  to = "/index.html"
  status = 200
`;

const INDEX_BODY = `<div id="zolaid-site-root" class="site-root">
  <header class="site-header">
    <div class="site-header-inner site-header-top">
      <a class="site-logo" href="#product">Your brand</a>
      <nav class="site-nav site-nav-primary" aria-label="Main">
        <a href="#product">Product</a>
        <a href="#overview">Overview</a>
        <a href="#features">Features</a>
        <a href="#apps">Apps</a>
        <a href="#pricing">Pricing</a>
        <a href="#download">Download</a>
      </nav>
      <div class="site-header-actions">
        <a href="#auth" class="site-signin-link">Sign in</a>
      </div>
    </div>
  </header>

  <main class="site-main">
    <section id="product" class="section section-product" aria-labelledby="product-heading">
      <h2 id="product-heading">Product</h2>
      <p class="section-lead">Top-level product value — edit <code>frontend/product.js</code> and <code>frontend/product.css</code>.</p>
    </section>

    <section id="overview" class="section section-hero" aria-labelledby="overview-heading">
      <h2 id="overview-heading">Overview</h2>
      <p class="section-lead">Built-in marketing shell — pair with <code>frontend/overview.js</code> / <code>frontend/overview.css</code>.</p>
    </section>

    <section id="features" class="section" aria-labelledby="features-heading">
      <h2 id="features-heading">Features</h2>
      <p class="section-lead">Feature grid — <code>frontend/features.js</code> / <code>frontend/features.css</code>.</p>
    </section>

    <section id="apps" class="section" aria-labelledby="apps-heading">
      <h2 id="apps-heading">Apps</h2>
      <p class="section-lead">Mobile, desktop, or embedded apps — <code>frontend/apps.js</code> / <code>frontend/apps.css</code>.</p>
    </section>

    <section id="pricing" class="section pricing-section" aria-labelledby="pricing-heading">
      <h2 id="pricing-heading">Pricing</h2>
      <p class="section-lead">Layout: <code>frontend/pricing.css</code> · behavior: <code>frontend/pricing.js</code> · Checkout: <code>frontend/stripe-integration.js</code> + <code>POST /api/create-checkout-session</code> on <code>../backend</code>.</p>
      <div class="pricing-grid" role="list">
        <article class="pricing-card" role="listitem">
          <h3 class="pricing-tier">Starter</h3>
          <p class="pricing-price"><span class="pricing-amount">$0</span><span class="pricing-period">/mo</span></p>
          <ul class="pricing-features">
            <li>Core features</li>
            <li>Community support</li>
          </ul>
          <button type="button" class="btn btn-secondary" data-stripe-checkout data-plan="starter" disabled aria-disabled="true">Included</button>
        </article>
        <article class="pricing-card pricing-card-featured" role="listitem">
          <h3 class="pricing-tier">Pro</h3>
          <p class="pricing-price"><span class="pricing-amount">$9.99</span><span class="pricing-period"> one-time demo</span></p>
          <ul class="pricing-features">
            <li>Stripe Checkout (payment or subscription)</li>
            <li>Webhook: <code>/api/stripe/webhook</code></li>
          </ul>
          <button type="button" class="btn btn-primary" data-stripe-checkout data-plan="pro">Pay with Stripe</button>
        </article>
        <article class="pricing-card" role="listitem">
          <h3 class="pricing-tier">Business</h3>
          <p class="pricing-price"><span class="pricing-amount">Custom</span></p>
          <ul class="pricing-features">
            <li>Volume, SSO, SLA</li>
            <li>Use <code>STRIPE_PRICE_ID</code> for catalog prices</li>
          </ul>
          <button type="button" class="btn btn-secondary" data-stripe-checkout data-plan="business">Contact sales</button>
        </article>
      </div>
      <p id="stripe-checkout-status" class="stripe-status" role="status" aria-live="polite"></p>
    </section>

    <section id="download" class="section" aria-labelledby="download-heading">
      <h2 id="download-heading">Download</h2>
      <p class="section-lead">Installers and release notes — <code>frontend/download.js</code> / <code>frontend/download.css</code>.</p>
    </section>

    <section id="teams" class="section" aria-labelledby="teams-heading">
      <h2 id="teams-heading">Teams</h2>
      <p class="section-lead"><code>frontend/teams.js</code> / <code>frontend/teams.css</code></p>
    </section>

    <section id="enterprise" class="section" aria-labelledby="enterprise-heading">
      <h2 id="enterprise-heading">Enterprise</h2>
      <p class="section-lead"><code>frontend/enterprise.js</code> / <code>frontend/enterprise.css</code></p>
    </section>

    <section id="education" class="section" aria-labelledby="education-heading">
      <h2 id="education-heading">Education</h2>
      <p class="section-lead"><code>frontend/education.js</code> / <code>frontend/education.css</code></p>
    </section>

    <section id="integrations" class="section" aria-labelledby="integrations-heading">
      <h2 id="integrations-heading">Integrations</h2>
      <p class="section-lead"><code>frontend/integrations.js</code> / <code>frontend/integrations.css</code> · API in <code>../backend</code>.</p>
    </section>

    <section id="resources" class="section" aria-labelledby="resources-heading">
      <h2 id="resources-heading">Resources</h2>
      <p class="section-lead">Hub for guides and downloads — <code>frontend/resources.js</code> / <code>frontend/resources.css</code>.</p>
    </section>

    <section id="docs" class="section" aria-labelledby="docs-heading">
      <h2 id="docs-heading">Docs</h2>
      <p class="section-lead"><code>frontend/docs.js</code> / <code>frontend/docs.css</code></p>
    </section>

    <section id="tech-articles" class="section" aria-labelledby="tech-articles-heading">
      <h2 id="tech-articles-heading">Tech Articles</h2>
      <p class="section-lead"><code>frontend/tech-articles.js</code> / <code>frontend/tech-articles.css</code></p>
    </section>

    <section id="events" class="section" aria-labelledby="events-heading">
      <h2 id="events-heading">Events</h2>
      <p class="section-lead"><code>frontend/events.js</code> / <code>frontend/events.css</code></p>
    </section>

    <section id="blog" class="section" aria-labelledby="blog-heading">
      <h2 id="blog-heading">Blog</h2>
      <p class="section-lead"><code>frontend/blog.js</code> / <code>frontend/blog.css</code></p>
    </section>

    <section id="support" class="section" aria-labelledby="support-heading">
      <h2 id="support-heading">Support</h2>
      <p class="section-lead"><code>frontend/support.js</code> / <code>frontend/support.css</code> · also <code>#contact</code>.</p>
    </section>

    <section id="company" class="section" aria-labelledby="company-heading">
      <h2 id="company-heading">Company</h2>
      <p class="section-lead">Mission, press, leadership — <code>frontend/company.js</code> / <code>frontend/company.css</code>.</p>
    </section>

    <section id="about" class="section about-section" aria-labelledby="about-heading">
      <h2 id="about-heading">About</h2>
      <p class="about-intro">Copy in <code>frontend/index.html</code>; logic <code>frontend/about.js</code>; styles <code>frontend/about.css</code>.</p>
      <ul class="about-highlights" id="about-highlights">
        <li>Mission-driven product</li>
        <li>Customer-first support</li>
        <li>Built for clarity</li>
      </ul>
    </section>

    <section id="careers" class="section" aria-labelledby="careers-heading">
      <h2 id="careers-heading">Careers</h2>
      <p class="section-lead"><code>frontend/careers.js</code> / <code>frontend/careers.css</code> — ATS links, open roles.</p>
    </section>

    <section id="auth" class="section auth-section" aria-labelledby="auth-heading">
      <h2 id="auth-heading">Sign in</h2>
      <p class="section-lead">OAuth entry points: <code>GET /auth/&lt;provider&gt;</code> on <code>../backend</code> (stubs). Wire real flows in the API (see <code>frontend/auth-integration.js</code>).</p>
      <div class="auth-actions" role="group" aria-label="Sign in with a social provider">
        <button type="button" class="btn btn-auth btn-auth-google" data-auth-demo="google">Continue with Google</button>
      </div>
    </section>

    <section id="contact" class="section contact-section" aria-labelledby="contact-heading">
      <h2 id="contact-heading">Contact</h2>
      <p class="section-lead">POST to <code>/api/contact</code> on the backend when ready (see <code>frontend/contact.js</code>).</p>
      <form id="contact-form" class="contact-form" novalidate>
        <label class="field">
          <span>Name</span>
          <input name="name" type="text" autocomplete="name" required placeholder="Jane Doe" />
        </label>
        <label class="field">
          <span>Email</span>
          <input name="email" type="email" autocomplete="email" required placeholder="you@example.com" />
        </label>
        <label class="field field-full">
          <span>Message</span>
          <textarea name="message" rows="4" required placeholder="How can we help?"></textarea>
        </label>
        <button type="submit" class="btn btn-primary">Send message</button>
        <p id="contact-form-status" class="form-status" role="status" aria-live="polite"></p>
      </form>
    </section>

    <section id="delete-my-data" class="section section-legal" aria-labelledby="delete-my-data-heading">
      <h2 id="delete-my-data-heading">Delete my data</h2>
      <p class="section-lead">Data-deletion flow — <code>frontend/delete-my-data.js</code> / <code>frontend/delete-my-data.css</code> (deleteMyData).</p>
    </section>

    <section id="terms" class="section section-legal" aria-labelledby="terms-heading">
      <h2 id="terms-heading">Terms</h2>
      <p class="section-lead"><code>frontend/terms.js</code> / <code>frontend/terms.css</code></p>
    </section>

    <section id="privacy" class="section section-legal" aria-labelledby="privacy-heading">
      <h2 id="privacy-heading">Privacy</h2>
      <p class="section-lead"><code>frontend/privacy.js</code> / <code>frontend/privacy.css</code></p>
    </section>
  </main>

  <footer class="site-footer">
    <div class="site-footer-inner">
      <div class="footer-columns" role="navigation" aria-label="Footer">
        <div class="footer-col">
          <p class="footer-col-title" id="footer-label-solutions">Solutions</p>
          <ul class="footer-col-list" aria-labelledby="footer-label-solutions">
            <li><a href="#teams">Teams</a></li>
            <li><a href="#enterprise">Enterprise</a></li>
            <li><a href="#education">Education</a></li>
            <li><a href="#integrations">Integrations</a></li>
          </ul>
        </div>
        <div class="footer-col">
          <p class="footer-col-title" id="footer-label-resources">Resources</p>
          <ul class="footer-col-list" aria-labelledby="footer-label-resources">
            <li><a href="#docs">Docs</a></li>
            <li><a href="#tech-articles">Tech Articles</a></li>
            <li><a href="#resources">Resources</a></li>
            <li><a href="#events">Events</a></li>
            <li><a href="#blog">Blog</a></li>
            <li><a href="#support">Support</a></li>
          </ul>
        </div>
        <div class="footer-col">
          <p class="footer-col-title" id="footer-label-company">Company</p>
          <ul class="footer-col-list" aria-labelledby="footer-label-company">
            <li><a href="#company">Company</a></li>
            <li><a href="#about">About</a></li>
            <li><a href="#careers">Careers</a></li>
            <li><a href="#contact">Contact</a></li>
          </ul>
        </div>
        <div class="footer-col">
          <p class="footer-col-title" id="footer-label-legal">Legal</p>
          <ul class="footer-col-list" aria-labelledby="footer-label-legal">
            <li><a href="#delete-my-data">Delete my data</a></li>
            <li><a href="#terms">Terms</a></li>
            <li><a href="#privacy">Privacy</a></li>
          </ul>
        </div>
      </div>
      <div class="footer-bottom">
        <span class="footer-copy">© __YEAR__ Your company</span>
      </div>
    </div>
  </footer>
</div>
`.replace("__YEAR__", String(new Date().getFullYear()));

const STYLES_GLOBAL = `/* frontend/styles.css — layout, tokens, header/footer */
:root {
  --z-color-bg: #0f1419;
  --z-color-surface: #1a2332;
  --z-color-text: #e8eef7;
  --z-color-muted: #94a3b8;
  --z-color-accent: #6366f1;
  --z-radius: 12px;
  --z-font: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
}

*, *::before, *::after { box-sizing: border-box; }

.site-root {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  font-family: var(--z-font);
  background: var(--z-color-bg);
  color: var(--z-color-text);
  line-height: 1.5;
}

.site-header {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(15, 20, 25, 0.92);
  backdrop-filter: blur(8px);
  position: sticky;
  top: 0;
  z-index: 10;
}

.site-header-inner {
  max-width: 1120px;
  margin: 0 auto;
  padding: 1rem 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.site-header-top {
  flex-wrap: wrap;
  row-gap: 0.75rem;
}

.site-header-top .site-nav-primary {
  flex: 1 1 auto;
  justify-content: center;
  min-width: 0;
}

.site-header-actions {
  flex-shrink: 0;
}

.site-signin-link {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.875rem;
  color: #fff;
  background: var(--z-color-accent);
  text-decoration: none;
  white-space: nowrap;
}

.site-signin-link:hover {
  filter: brightness(1.08);
  color: #fff;
}

@media (max-width: 720px) {
  .site-header-top .site-nav-primary {
    order: 3;
    flex: 1 1 100%;
    justify-content: flex-start;
  }
}

.site-logo {
  font-weight: 800;
  color: var(--z-color-text);
  text-decoration: none;
}

.site-nav {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.site-nav a {
  color: var(--z-color-muted);
  text-decoration: none;
  font-size: 0.9rem;
}

.site-nav a:hover {
  color: var(--z-color-text);
}

.site-main {
  flex: 1;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 2rem 1.25rem 3rem;
}

.section-hero {
  background: linear-gradient(145deg, rgba(99, 102, 241, 0.12), var(--z-color-surface));
}

.section-legal .section-lead {
  font-size: 0.9rem;
}

.section {
  margin-bottom: 3rem;
  padding: 1.5rem;
  background: var(--z-color-surface);
  border-radius: var(--z-radius);
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.section h2 {
  margin: 0 0 0.75rem;
  font-size: 1.35rem;
}

.section-lead {
  margin: 0 0 1rem;
  color: var(--z-color-muted);
  font-size: 0.95rem;
}

.site-footer {
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 2rem 1.25rem 1.5rem;
}

.site-footer-inner {
  max-width: 1120px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 1.75rem;
  font-size: 0.85rem;
  color: var(--z-color-muted);
}

.footer-columns {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(9.5rem, 1fr));
  gap: 1.5rem 2rem;
  width: 100%;
}

.footer-col {
  min-width: 0;
}

.footer-col-title {
  margin: 0 0 0.65rem;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--z-color-muted);
}

.footer-col-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.footer-col-list a {
  color: var(--z-color-text);
  text-decoration: none;
  font-size: 0.875rem;
  opacity: 0.85;
}

.footer-col-list a:hover {
  opacity: 1;
  color: var(--z-color-accent);
}

.footer-bottom {
  padding-top: 1.25rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.footer-copy {
  display: block;
  font-size: 0.8rem;
  color: var(--z-color-muted);
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.6rem 1rem;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  border: 1px solid transparent;
  cursor: pointer;
  font-family: inherit;
}

.btn-primary {
  background: var(--z-color-accent);
  color: #fff;
}

.btn-secondary {
  background: rgba(255, 255, 255, 0.08);
  color: var(--z-color-text);
  border-color: rgba(255, 255, 255, 0.12);
}

.btn-google {
  background: #fff;
  color: #1f2937;
}
`;

const AUTH_CSS = `/* frontend/auth-integration.css */
.auth-section .auth-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.btn-auth {
  min-width: 12.5rem;
  justify-content: center;
}

.btn-auth-facebook {
  background: #1877f2;
  color: #fff;
}

.btn-auth-google {
  background: #fff;
  color: #1f2937;
}

.btn-auth-microsoft {
  background: #2f2f2f;
  color: #fff;
  border-color: rgba(255, 255, 255, 0.2);
}

.btn-auth-apple {
  background: #000;
  color: #fff;
  border-color: rgba(255, 255, 255, 0.25);
}
`;

const AUTH_JS = `/* frontend/auth-integration.js — OAuth stubs: GET /auth/:provider */
(function () {
  var API_BASE =
    (typeof window !== "undefined" && window.__STARTER_PUBLIC__ && window.__STARTER_PUBLIC__.apiBase) ||
    (typeof window !== "undefined" && window.__API_BASE__) ||
    "http://localhost:3001";
  API_BASE = String(API_BASE).trim().replace(/\\/$/, "");
  var PROVIDERS = { facebook: 1, google: 1, microsoft: 1, apple: 1 };

  function toast(msg) {
    try {
      var t = document.createElement("div");
      t.textContent = msg;
      t.style.cssText = "position:fixed;bottom:16px;right:16px;padding:12px 16px;background:#1e293b;color:#fff;border-radius:10px;font:600 13px system-ui;z-index:9999;max-width:min(320px,100vw - 32px);";
      document.body.appendChild(t);
      setTimeout(function () { t.remove(); }, 3800);
    } catch (e) {
      alert(msg);
    }
  }

  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-auth-demo]");
    if (!btn) return;
    var provider = (btn.getAttribute("data-auth-demo") || "").toLowerCase();
    if (!PROVIDERS[provider]) {
      toast("Unknown provider: " + provider);
      return;
    }
    var url = API_BASE.replace(/\\/$/, "") + "/auth/" + provider;
    fetch(url, { method: "GET", mode: "cors" })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.error || "auth stub failed");
          var hint = data.hint || data.nextStep || "Wire OAuth in the backend.";
          toast(provider + ": " + hint);
        });
      })
      .catch(function () {
        toast("Start API: cd backend && npm install && npm start — then retry " + provider + " (" + url + ")");
      });
  });
})();
`;

const ABOUT_CSS = `/* frontend/about.css */
.about-intro {
  max-width: 52ch;
}

.about-highlights {
  margin: 1rem 0 0;
  padding-left: 1.25rem;
  color: var(--z-color-muted, #94a3b8);
}

.about-highlights li {
  margin: 0.35rem 0;
}
`;

const ABOUT_JS = `/* frontend/about.js */
(function () {
  var root = document.getElementById("about-highlights");
  if (!root) return;
  root.setAttribute("data-zolaid-about", "ready");
})();
`;

const CONTACT_CSS = `/* frontend/contact.css */
.contact-form {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  max-width: 640px;
}

@media (max-width: 600px) {
  .contact-form {
    grid-template-columns: 1fr;
  }
}

.contact-form .field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  font-size: 0.85rem;
  color: var(--z-color-muted, #94a3b8);
}

.contact-form .field-full {
  grid-column: 1 / -1;
}

.contact-form input,
.contact-form textarea {
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(0, 0, 0, 0.25);
  color: inherit;
  font: inherit;
}

.form-status {
  grid-column: 1 / -1;
  min-height: 1.25rem;
  font-size: 0.85rem;
  color: var(--z-color-accent, #6366f1);
  margin: 0;
}
`;

const CONTACT_JS = `/* frontend/contact.js — backend POST /api/contact */
(function () {
  var API_BASE =
    (typeof window !== "undefined" && window.__STARTER_PUBLIC__ && window.__STARTER_PUBLIC__.apiBase) ||
    (typeof window !== "undefined" && window.__API_BASE__) ||
    "http://localhost:3001";
  API_BASE = String(API_BASE).trim().replace(/\\/$/, "");
  var form = document.getElementById("contact-form");
  var statusEl = document.getElementById("contact-form-status");
  if (!form || !statusEl) return;

  form.addEventListener("submit", function (e) {
    e.preventDefault();
    if (!form.reportValidity()) return;
    var fd = new FormData(form);
    var payload = {
      name: fd.get("name"),
      email: fd.get("email"),
      message: fd.get("message"),
    };
    fetch(API_BASE + "/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.error || "Request failed");
          statusEl.textContent = data.message || "Sent.";
        });
      })
      .catch(function () {
        statusEl.textContent = "Offline demo: start backend (cd backend && npm install && npm start) or wire another API.";
      });
  });
})();
`;

const PRICING_CSS = `/* frontend/pricing.css — #pricing section + tier cards */
#pricing.section {
  scroll-margin-top: 5rem;
}

.pricing-section .pricing-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.25rem;
  margin-top: 1.25rem;
  list-style: none;
  padding: 0;
  margin-left: 0;
  margin-right: 0;
}

.pricing-card {
  padding: 1.25rem;
  border-radius: var(--z-radius, 12px);
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.pricing-card-featured {
  border-color: rgba(99, 102, 241, 0.45);
  box-shadow: 0 0 0 1px rgba(99, 102, 241, 0.12);
}

.pricing-tier {
  margin: 0;
  font-size: 1.1rem;
}

.pricing-price {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 800;
}

.pricing-period {
  font-weight: 500;
  font-size: 0.85rem;
  color: var(--z-color-muted, #94a3b8);
}

.pricing-features {
  margin: 0;
  padding-left: 1.1rem;
  color: var(--z-color-muted, #94a3b8);
  font-size: 0.9rem;
  flex: 1;
}

.pricing-features li {
  margin: 0.35rem 0;
}

.pricing-card .btn {
  margin-top: auto;
  align-self: flex-start;
}
`;

const PRICING_JS = `/* frontend/pricing.js */
(function () {
  var el = document.getElementById("pricing");
  if (el) el.setAttribute("data-section-script", "pricing.js");
})();
`;

const STRIPE_CSS = `/* frontend/stripe-integration.css — Checkout status line */
.stripe-status {
  margin-top: 1rem;
  font-size: 0.9rem;
  color: var(--z-color-accent, #6366f1);
  min-height: 1.25rem;
}
`;

const STRIPE_JS = `/* frontend/stripe-integration.js — Checkout session + webhook on backend */
(function () {
  var API_BASE =
    (typeof window !== "undefined" && window.__STARTER_PUBLIC__ && window.__STARTER_PUBLIC__.apiBase) ||
    (typeof window !== "undefined" && window.__API_BASE__) ||
    "http://localhost:3001";
  API_BASE = String(API_BASE).trim().replace(/\\/$/, "");
  var statusEl = document.getElementById("stripe-checkout-status");

  function setStatus(msg) {
    if (statusEl) statusEl.textContent = msg || "";
  }

  document.addEventListener("click", function (e) {
    var btn = e.target.closest("[data-stripe-checkout]");
    if (!btn || btn.disabled) return;
    var plan = (btn.getAttribute("data-plan") || "").toLowerCase();
    if (plan === "business") {
      setStatus("Business tier: use the Contact section or wire a Calendly / sales form.");
      return;
    }
    if (plan !== "pro") return;

    setStatus("Creating Checkout session…");
    var payload = { plan: "pro" };
    if (window.__STRIPE_PRICE_ID__) payload.priceId = window.__STRIPE_PRICE_ID__;
    if (typeof window !== "undefined" && window.location && window.location.origin) {
      var u = window.location;
      var base = u.origin + (u.pathname || "/") + (u.search || "");
      var join = base.indexOf("?") >= 0 ? "&" : "?";
      payload.successUrl = base + join + "stripe=success&session_id={CHECKOUT_SESSION_ID}";
      payload.cancelUrl = base + join + "stripe=cancel";
    }

    fetch(API_BASE.replace(/\\/$/, "") + "/api/create-checkout-session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw new Error(data.error || "checkout failed");
          if (data.demo && !data.url) {
            setStatus(data.message || "Demo: add STRIPE_SECRET_KEY to backend .env and npm install.");
            return;
          }
          if (data.url) {
            window.location.href = data.url;
            return;
          }
          setStatus("Unexpected response from API.");
        });
      })
      .catch(function (err) {
        setStatus("API unreachable — start backend (cd backend && npm install && npm start). " + (err && err.message ? err.message : ""));
      });
  });

  if (typeof window !== "undefined" && window.location && /[?&]stripe=success/.test(window.location.search)) {
    setStatus("Returned from Stripe Checkout (verify session on the server).");
  }
  if (typeof window !== "undefined" && window.location && /[?&]stripe=cancel/.test(window.location.search)) {
    setStatus("Checkout canceled.");
  }
})();
`;

const APP_JS = `/* frontend/app.js
 * Central route map — same *idea* as React Router: one table connects URLs to "views".
 * This site is static HTML: each "page" is a <section id="...">; section scripts live in frontend/<id>.js.
 *
 * React equivalent (your App.js pattern):
 *   <Routes>
 *     <Route path="/" element={<HomePage />} />
 *     <Route path="/pricing" element={<Pricing />} />
 *     ...
 *   </Routes>
 *
 * Here: path → section id (scroll target). Use hash URLs like #/pricing or #/docs so static hosts work without a dev server rewrite.
 * Plain hashes like #pricing still work (native in-page jump).
 */
(function () {
  document.documentElement.setAttribute("data-zolaid-app", "starter-monorepo");
  document.documentElement.setAttribute("data-starter-config", "frontend/js/config.js (public API URL only)");

  /** Pathname (lowercase, no query) → section id in index.html */
  var ROUTES = {
    "": "product",
    "/": "product",
    "/chatbot": "product",
    "/chatbot-page": "product",
    "/techarticlespage": "tech-articles",
    "/login": "auth",
    "/auth": "auth",
    "/privateroute": "auth",
    "/features": "features",
    "/enterprise": "enterprise",
    "/sidebarsearch": "docs",
    "/imageeditor": "apps",
    "/videoeditor": "apps",
    "/ide": "docs",
    "/resources": "resources",
    "/roadmap": "overview",
    "/vrpreview": "apps",
    "/templates": "resources",
    "/impact": "company",
    "/campaigns": "events",
    "/projects": "company",
    "/moderation": "support",
    "/pricing": "pricing",
    "/events": "events",
    "/success": "pricing",
    "/privacypolicy": "privacy",
    "/termsofservice": "terms",
    "/delete-data": "delete-my-data",
    "/contact": "contact",
    "/docs": "docs",
    "/auth/microsoft/callback": "auth",
    "/auth/apple/callback": "auth",
    "/auth/facebook/callback": "auth",
    "/about": "about",
    "/careers": "careers",
    "/overview": "overview",
    "/download": "download",
    "/teams": "teams",
    "/education": "education",
    "/integrations": "integrations",
    "/apps": "apps",
    "/blog": "blog",
    "/support": "support",
  };

  var ROUTE_TITLES = {
    "/": "Home",
    "/pricing": "Pricing",
    "/docs": "Docs",
    "/contact": "Contact",
    "/auth": "Sign in",
  };

  function normalizePath(hash) {
    var h = String(hash || "").replace(/^#/, "");
    if (h.indexOf("/") !== 0) return null;
    var path = h.split("?")[0].toLowerCase();
    if (path.length > 1 && path.slice(-1) === "/") path = path.slice(0, -1);
    return path || "/";
  }

  function scrollToSection(id) {
    var el = document.getElementById(id);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }

  function applyHashRoute() {
    var path = normalizePath(typeof location !== "undefined" ? location.hash : "");
    if (path == null) return;
    var sectionId = ROUTES[path];
    if (!sectionId) return;
    scrollToSection(sectionId);
    var t = ROUTE_TITLES[path];
    if (t && typeof document !== "undefined") document.title = t + " · Starter";
  }

  if (typeof window !== "undefined") {
    window.__STARTER_ROUTES__ = ROUTES;
    window.addEventListener("hashchange", applyHashRoute);
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", applyHashRoute);
    } else {
      applyHashRoute();
    }
  }
})();
`;

const ROOT_README = `# Zolaid starter project (built-in)

This tree is **pre-generated** in Zolaid Computer so you can **refine with AI** instead of scaffolding.

## Layout

- **frontend/** — static site, \`js/config.js\` (public API URL only), \`js/api.js\`, \`public/\` (robots, manifest), deploy stubs (\`vercel.json\`, \`netlify.toml\`).
- **backend/** — Express app split into \`app.js\`, \`routes/*\`, \`middleware/*\`, \`config/\`. Secrets **only** in \`.env\` (see \`.env.example\` — empty placeholders).

## Run the API

\`\`\`bash
cd backend
cp .env.example .env   # edit locally; never commit .env
npm install
npm start
# default http://localhost:3001
\`\`\`

## Preview / ship the frontend

Zolaid **Preview** bundles \`frontend/js/config.js\` first, then feature scripts. \`frontend/app.js\` maps \`#/path\` URLs to page sections (same role as React Router). Set \`window.__API_BASE__\` to your deployed API origin in production.

## Security

Do **not** put Stripe keys, OAuth secrets, or JWT signing keys in frontend files. Use \`backend/.env\` or your host’s secret manager.
`;

const FRONTEND_README = `# frontend

- \`index.html\` — marketing shell (sections + auth + Stripe + legal).
- \`js/config.js\` — **public** \`apiBase\` only (\`window.__API_BASE__\` override). No secret keys.
- \`js/api.js\` — \`window.__starterApi.json()\` helper for JSON APIs.
- \`public/robots.txt\`, \`public/manifest.webmanifest\` — SEO / PWA stubs.
- \`app.js\` — **route table** (path → \`#section\` id), like a React \`<Routes>\` map; use \`#/pricing\`, \`#/docs\`, etc.
- \`styles.css\`, \`auth-integration.*\`, \`stripe-integration.*\`, \`about.*\`, \`contact.*\`.

Live Preview loads JS in the order listed in the kit (\`FRONTEND_JS_ORDER\`).
`;

const BACKEND_PACKAGE = `{
  "name": "zolaid-starter-api",
  "private": true,
  "version": "0.0.0",
  "type": "module",
  "scripts": {
    "start": "node server.js",
    "dev": "node server.js"
  },
  "dependencies": {
    "cors": "^2.8.5",
    "dotenv": "^16.4.5",
    "express": "^4.22.1",
    "stripe": "^17.5.0"
  }
}
`;

const BACKEND_GITIGNORE = `node_modules/
.env
.env.local
.env.*.local
*.log
.DS_Store
`;

const BACKEND_MW_NOTFOUND = `/** 404 JSON — register after all routes */
export function notFoundHandler(req, res) {
  res.status(404).json({ error: "Not found", path: req.path });
}
`;

const BACKEND_MW_ERROR = `/** Last middleware — do not log secret values */
export function errorHandler(err, req, res, next) {
  if (res.headersSent) return next(err);
  console.error("[starter-api]", err.message || err);
  const status = Number(err.status) >= 400 && Number(err.status) < 600 ? Number(err.status) : 500;
  const message = status === 500 ? "Internal error" : String(err.message || "Error");
  res.status(status).json({ error: message });
}
`;

const BACKEND_ROUTE_HEALTH = `import { Router } from "express";

const r = Router();
r.get("/health", (_req, res) => {
  res.json({ ok: true, service: "zolaid-starter-api", ts: new Date().toISOString() });
});
export default r;
`;

const BACKEND_ROUTE_AUTH = `import { Router } from "express";

const r = Router();
const PROVIDERS = new Set(["facebook", "google", "microsoft", "apple"]);

r.get("/auth/:provider", (req, res) => {
  const p = String(req.params.provider || "").toLowerCase();
  if (!PROVIDERS.has(p)) return res.status(404).json({ error: "unknown provider" });
  res.json({
    demo: true,
    provider: p,
    hint:
      "Redirect the user to the provider authorize URL; exchange the code server-side (never trust tokens from the browser alone).",
  });
});
export default r;
`;

const BACKEND_ROUTE_CONTACT = `import { Router } from "express";

const r = Router();
r.post("/api/contact", (req, res) => {
  const { name, email, message } = req.body || {};
  if (!email || typeof email !== "string") {
    return res.status(400).json({ error: "email required" });
  }
  console.log("[contact]", {
    name: String(name || "").slice(0, 120),
    email: String(email).slice(0, 120),
    message: String(message || "").slice(0, 2000),
  });
  res.json({
    ok: true,
    demo: true,
    message: "Received (stub). Add email/DB persistence next.",
  });
});
export default r;
`;

const BACKEND_STRIPE_WEBHOOK = `/** Raw body — keys only via process.env (.env locally, platform secrets in prod) */
export async function stripeWebhookHandler(req, res) {
  const whSecret = process.env.STRIPE_WEBHOOK_SECRET;
  const apiKey = process.env.STRIPE_SECRET_KEY;
  if (!whSecret || !apiKey) {
    console.log("[stripe-webhook] demo mode — set STRIPE_WEBHOOK_SECRET and STRIPE_SECRET_KEY in .env (never commit .env)");
    return res.json({ received: true, demo: true });
  }
  try {
    const Stripe = (await import("stripe")).default;
    const stripe = new Stripe(apiKey);
    const sig = req.headers["stripe-signature"];
    const event = stripe.webhooks.constructEvent(req.body, sig, whSecret);
    console.log("[stripe webhook]", event.type);
    res.json({ received: true });
  } catch (err) {
    console.error("[stripe webhook]", err.message);
    res.status(400).send("Webhook Error: " + err.message);
  }
}
`;

const BACKEND_STRIPE_CHECKOUT = `/** Checkout — STRIPE_SECRET_KEY from environment only */
export async function createCheckoutSession(req, res) {
  const secret = process.env.STRIPE_SECRET_KEY;
  if (!secret) {
    return res.json({
      ok: true,
      demo: true,
      message:
        "Demo: set STRIPE_SECRET_KEY in .env (optional STRIPE_PRICE_ID). Never commit secrets. Run: npm install && npm start.",
    });
  }
  try {
    const Stripe = (await import("stripe")).default;
    const stripe = new Stripe(secret);
    const fallbackBase = (process.env.FRONTEND_ORIGIN || "http://127.0.0.1:5500").replace(/\\/$/, "");
    let success_url = req.body?.successUrl;
    let cancel_url = req.body?.cancelUrl;
    if (!success_url || !String(success_url).includes("{CHECKOUT_SESSION_ID}")) {
      success_url = fallbackBase + "/?session_id={CHECKOUT_SESSION_ID}&stripe=success";
    }
    if (!cancel_url) cancel_url = fallbackBase + "/?stripe=cancel";

    const priceId = req.body?.priceId || process.env.STRIPE_PRICE_ID;
    const line_items = priceId
      ? [{ price: priceId, quantity: 1 }]
      : [
          {
            price_data: {
              currency: "usd",
              product_data: { name: "Pro (starter demo)" },
              unit_amount: 999,
            },
            quantity: 1,
          },
        ];

    const session = await stripe.checkout.sessions.create({
      mode: "payment",
      line_items,
      success_url,
      cancel_url,
    });
    res.json({ url: session.url });
  } catch (e) {
    console.error("[checkout]", e);
    res.status(500).json({ error: e.message || "checkout failed" });
  }
}
`;

const BACKEND_APP = `import express from "express";
import cors from "cors";
import healthRouter from "./routes/health.js";
import authRouter from "./routes/auth.js";
import contactRouter from "./routes/contact.js";
import { stripeWebhookHandler } from "./routes/stripeWebhook.js";
import { createCheckoutSession } from "./routes/stripeCheckout.js";
import { notFoundHandler } from "./middleware/notFound.js";
import { errorHandler } from "./middleware/errorHandler.js";

export function createApp() {
  const app = express();

  app.use(cors({ origin: true, credentials: true }));

  app.post(
    "/api/stripe/webhook",
    express.raw({ type: "application/json" }),
    (req, res, next) => {
      Promise.resolve(stripeWebhookHandler(req, res)).catch(next);
    }
  );

  app.use(express.json({ limit: "1mb" }));

  app.use(healthRouter);
  app.use(authRouter);
  app.use(contactRouter);

  app.post("/api/create-checkout-session", (req, res, next) => {
    Promise.resolve(createCheckoutSession(req, res)).catch(next);
  });

  app.use(notFoundHandler);
  app.use(errorHandler);
  return app;
}
`;

const BACKEND_SERVER = `/**
 * Entry — loads .env from disk (never commit .env). See .env.example for variable names only.
 */
import "dotenv/config";
import { createApp } from "./app.js";

const PORT = Number(process.env.PORT) || 3001;
const app = createApp();

app.listen(PORT, "0.0.0.0", () => {
  console.log("Starter API listening on http://localhost:" + PORT);
});
`;

const BACKEND_ENV_EXAMPLE = `# Copy to .env — leave values empty in git; fill locally or in your host secret store.
# Never paste keys into source files or commit .env

NODE_ENV=development
PORT=3001

# Public URL of your static frontend (Stripe return URLs)
FRONTEND_ORIGIN=http://127.0.0.1:5500

# Database (optional)
DATABASE_URL=

# Session / JWT (optional — generate random strings in production)
SESSION_SECRET=
JWT_SECRET=

# OAuth client credentials (optional — each provider)
GOOGLE_CLIENT_ID=
GOOGLE_CLIENT_SECRET=
FACEBOOK_APP_ID=
FACEBOOK_APP_SECRET=
MICROSOFT_CLIENT_ID=
MICROSOFT_CLIENT_SECRET=
APPLE_CLIENT_ID=
APPLE_TEAM_ID=
APPLE_KEY_ID=
APPLE_PRIVATE_KEY_PATH=

# Stripe (optional — live Checkout + webhooks)
STRIPE_SECRET_KEY=
STRIPE_WEBHOOK_SECRET=
STRIPE_PRICE_ID=
`;

const BACKEND_CONFIG_ENV = `/**
 * Typed env accessors — values always from process.env, never hardcoded secrets.
 */
export function envString(name, fallback = "") {
  const v = process.env[name];
  return v != null && String(v).trim() !== "" ? String(v).trim() : fallback;
}

export function envPort(defaultPort = 3001) {
  const n = Number(process.env.PORT);
  return Number.isFinite(n) && n > 0 ? n : defaultPort;
}
`;

const FRONTEND_PACKAGE = `{
  "name": "zolaid-starter-site",
  "private": true,
  "version": "0.0.0",
  "description": "Static site — use Zolaid Preview or any static host"
}
`;

function buildStarterMap() {
  return {
    ".gitignore": ROOT_GITIGNORE,
    "vercel.json": VERCEL_JSON,
    "netlify.toml": NETLIFY_TOML,
    "README.md": ROOT_README,
    "frontend/README.md": FRONTEND_README,
    "frontend/.gitignore": FRONTEND_GITIGNORE,
    "frontend/package.json": FRONTEND_PACKAGE,
    [FRONTEND_HTML]: INDEX_BODY,
    "frontend/js/config.js": CONFIG_JS,
    "frontend/js/api.js": API_CLIENT_JS,
    "frontend/styles.css": STYLES_GLOBAL,
    ...buildStarterSectionFiles(),
    "frontend/pricing.css": PRICING_CSS,
    "frontend/pricing.js": PRICING_JS,
    "frontend/stripe-integration.css": STRIPE_CSS,
    "frontend/stripe-integration.js": STRIPE_JS,
    "frontend/auth-integration.css": AUTH_CSS,
    "frontend/auth-integration.js": AUTH_JS,
    "frontend/about.css": ABOUT_CSS,
    "frontend/about.js": ABOUT_JS,
    "frontend/contact.css": CONTACT_CSS,
    "frontend/contact.js": CONTACT_JS,
    "frontend/app.js": APP_JS,
    "frontend/public/robots.txt": FRONTEND_PUBLIC_ROBOTS,
    "frontend/public/manifest.webmanifest": FRONTEND_MANIFEST,
    "frontend/assets/README.txt": FRONTEND_ASSETS_README,
    "backend/.gitignore": BACKEND_GITIGNORE,
    "backend/package.json": BACKEND_PACKAGE,
    "backend/server.js": BACKEND_SERVER,
    "backend/app.js": BACKEND_APP,
    "backend/config/env.js": BACKEND_CONFIG_ENV,
    "backend/middleware/notFound.js": BACKEND_MW_NOTFOUND,
    "backend/middleware/errorHandler.js": BACKEND_MW_ERROR,
    "backend/routes/health.js": BACKEND_ROUTE_HEALTH,
    "backend/routes/auth.js": BACKEND_ROUTE_AUTH,
    "backend/routes/contact.js": BACKEND_ROUTE_CONTACT,
    "backend/routes/stripeWebhook.js": BACKEND_STRIPE_WEBHOOK,
    "backend/routes/stripeCheckout.js": BACKEND_STRIPE_CHECKOUT,
    "backend/.env.example": BACKEND_ENV_EXAMPLE,
    "backend/README.md":
      "# backend\n\n1. `cp .env.example .env` — fill **locally**; never commit `.env`.\n2. `npm install` then `npm start`.\n\n## Layout\n\n- `server.js` — entry (`dotenv/config`)\n- `app.js` — Express factory\n- `routes/` — health, auth, contact, Stripe checkout + webhook handler modules\n- `middleware/` — 404 + errors\n- `config/env.js` — env helpers (no secret literals)\n\n## Endpoints\n\n- `GET /health`\n- `GET /auth/:provider`\n- `POST /api/contact`\n- `POST /api/create-checkout-session`\n- `POST /api/stripe/webhook` (raw JSON body)\n",
  };
}

export const IDE_SITE_STARTER_FILES = Object.freeze(buildStarterMap());

export function getIdeSiteStarterKit() {
  return { ...IDE_SITE_STARTER_FILES };
}

/** Build Live Preview triple from frontend/* (order matches website generator). */
export function previewTripleFromStarterKit(kit = IDE_SITE_STARTER_FILES) {
  const html = String(kit[FRONTEND_HTML] || "");
  const css = FRONTEND_CSS_ORDER.map((k) => String(kit[k] || "").trim())
    .filter(Boolean)
    .join("\n\n");
  const js = FRONTEND_JS_ORDER.map((k) => String(kit[k] || "").trim())
    .filter(Boolean)
    .join("\n\n");
  return { html, css, js };
}

/** `<section id="…">` ids in the built-in `frontend/index.html` (IDE AI uses this as ground truth). */
export const IDE_STARTER_KNOWN_SECTIONS = [
  "product",
  "overview",
  "features",
  "apps",
  "pricing",
  "download",
  "teams",
  "enterprise",
  "education",
  "integrations",
  "resources",
  "docs",
  "tech-articles",
  "events",
  "blog",
  "support",
  "company",
  "about",
  "careers",
  "auth",
  "contact",
  "delete-my-data",
  "terms",
  "privacy",
];

/**
 * Compact architecture summary for the IDE AI (repo search /dev tools do not see Generated files).
 * Pair with actual `generatedFiles` snippets in DeveloperIDE.
 */
export function getIdeStarterAiContextDigest() {
  const cssList = FRONTEND_CSS_ORDER.map((k) => k.replace(/^frontend\//, "")).join(", ");
  const jsList = FRONTEND_JS_ORDER.map((k) => k.replace(/^frontend\//, "")).join(", ");
  return [
    "=== Zolaid built-in starter (in-memory Generated files, not on-disk repo) ===",
    "One-page marketing shell: frontend/index.html with <section id=\"…\"> regions.",
    "Section ids: " + IDE_STARTER_KNOWN_SECTIONS.join(", ") + ".",
    "Live Preview merges CSS files in order, then JS (changing order breaks the bundle).",
    "CSS order: " + cssList,
    "JS order: " + jsList,
    "Cross-cutting: frontend/styles.css (layout/tokens), frontend/app.js (hash routes → sections), frontend/js/config.js + frontend/js/api.js (public API base + fetch helper).",
    "Commerce: frontend/pricing.* + frontend/stripe-integration.* → backend Stripe routes.",
    "Auth UI: frontend/auth-integration.* + #auth → GET /auth/:provider on backend (stubs).",
    "Contact: frontend/contact.* + #contact → POST /api/contact.",
    "Backend: backend/server.js loads dotenv; backend/app.js mounts health, auth, contact, Stripe checkout + raw webhook.",
    "Refurbish tasks: edit existing paths above; keep section ids and bundle order unless the user wants a new architecture.",
    "================================================================",
  ].join("\n");
}

export { FRONTEND_HTML, FRONTEND_CSS_ORDER, FRONTEND_JS_ORDER };
